import React, { useState } from 'react';
import Alert from 'antd/lib/alert';
import Button from 'antd/lib/button';
import Modal from 'antd/lib/modal';
import Space from 'antd/lib/space';
import Table, { ColumnsType } from 'antd/lib/table';
import Tooltip from 'antd/lib/tooltip';
import Typography from 'antd/lib/typography';
import CheckCircleFilled from '@ant-design/icons/lib/icons/CheckCircleFilled';
import SyncOutlined from '@ant-design/icons/lib/icons/SyncOutlined';
import WarningFilled from '@ant-design/icons/lib/icons/WarningFilled';
import { defaultDateFormat } from 'src/domain/common/dateFormatters';
import { getComputerFormattedTime } from 'src/domain/date/date';
import { MINUTE_MS } from 'src/domain/commonConst';
import { PRIMARY_COLOR } from 'src/theme';
import { EMPTY_VALUE } from 'src/components/customer-utility-bill/billStyles';
import { BillStatusTag } from 'src/components/customer-utility-bill/BillStatusTag';
import { useCustomerUtilityBillsQuery } from 'src/domain/customer-utility-bill/queries';
import {
    ICustomerUtilityBill,
    displayRateClass,
    isBillReparsable,
    isBillVerifiable,
    isReparseInProgress,
} from 'src/domain/customer-utility-bill/interface';
import { IPagination } from 'src/domain/IPagination';

const { Text } = Typography;

export type BillBatchAction = 'verify' | 'unverify' | 'reparse';

const plural = (count: number, noun: string): string => `${count} ${noun}${count === 1 ? '' : 's'}`;

const allReparsesDone = (bills: ICustomerUtilityBill[]): boolean => bills.every(bill => !isReparseInProgress(bill));

// Matches the "Billing Period" column format in the main bills table (billColumns.tsx).
const BILL_DATE_FORMAT = 'MMM d, yyyy';

const MODAL_COPY: Record<BillBatchAction, { title: (count: number) => string; confirmLabel: string; ineligibleReason: string }> = {
    verify: {
        title: count => `Verify ${plural(count, 'Customer Utility Bill')}`,
        confirmLabel: 'Verify',
        ineligibleReason: 'Already verified, or not yet parsed with data',
    },
    unverify: {
        title: count => `Unverify ${plural(count, 'Customer Utility Bill')}`,
        confirmLabel: 'Unverify',
        ineligibleReason: 'Not currently verified',
    },
    reparse: {
        title: count => `Re-parse ${plural(count, 'Customer Utility Bill')}`,
        confirmLabel: 'Re-parse',
        ineligibleReason: "Can't be re-parsed in its current status",
    },
};

// Mirrors the backend's eligibility gate so the operator sees the same skips before submitting, not after.
const isRowEligible = (row: ICustomerUtilityBill, action: BillBatchAction): boolean => {
    switch (action) {
        case 'verify':
            return isBillVerifiable(row) && !row.verifiedAt;
        case 'unverify':
            return !!row.verifiedAt;
        case 'reparse':
            return isBillReparsable(row);
    }
};

interface Row extends ICustomerUtilityBill {
    eligible: boolean;
}

interface Props {
    action: BillBatchAction;
    rows: ICustomerUtilityBill[];
    // Resolves with the ids the batch endpoint accepted (e.g. reparsedIds), used to decide what to track.
    onConfirm: () => Promise<string[]>;
    onClose: () => void;
}

// Verify/unverify close on confirm (a synchronous DB update). Re-parse instead switches into a live-tracking
// view, polling the accepted ids until every one reaches a terminal status.
export const BillsBatchActionModal: React.FC<Props> = ({ action, rows, onConfirm, onClose }) => {
    const copy = MODAL_COPY[action];
    // Snapshot once: confirming clears the page's selection, which would otherwise wipe `rows` mid-tracking.
    const [initialRows] = useState(rows);
    const [confirming, setConfirming] = useState(false);
    // null = pre-confirm preview; non-null (possibly empty) = tracking the ids the endpoint accepted.
    const [trackedIds, setTrackedIds] = useState<string[] | null>(null);
    const isTracking = trackedIds !== null;
    const trackedCount = trackedIds?.length ?? 0;

    const { data: page } = useCustomerUtilityBillsQuery(
        { ids: (trackedIds ?? []).join(',') },
        {
            enabled: isTracking,
            refetchInterval: (result?: IPagination<ICustomerUtilityBill>) =>
                result?.data.length && allReparsesDone(result.data) ? false : MINUTE_MS,
        }
    );
    const liveBills = page?.data;
    const allDone = isTracking && !!liveBills?.length && allReparsesDone(liveBills);

    const handleConfirm = async () => {
        setConfirming(true);
        try {
            const acceptedIds = await onConfirm();
            if (action === 'reparse' && acceptedIds.length > 0) {
                setTrackedIds(acceptedIds);
            } else {
                onClose();
            }
        } finally {
            setConfirming(false);
        }
    };

    const liveById = new Map((liveBills ?? []).map(bill => [bill.id, bill]));
    // Frozen from the pre-confirm snapshot; recomputing from live data would flicker the icon mid-poll.
    const tableRows: Row[] = initialRows.map(row => ({ ...row, ...liveById.get(row.id), eligible: isRowEligible(row, action) }));
    const ineligibleCount = tableRows.filter(row => !row.eligible).length;

    const columns: ColumnsType<Row> = [
        {
            title: '',
            dataIndex: 'eligible',
            width: 32,
            fixed: 'left',
            render: (eligible: boolean) =>
                !eligible && (
                    <Tooltip title={copy.ineligibleReason}>
                        <WarningFilled style={{ color: 'orange' }} />
                    </Tooltip>
                ),
        },
        { title: 'Status', dataIndex: 'processingStatus', render: (_, r) => <BillStatusTag status={r.processingStatus} /> },
        {
            title: 'Verified',
            dataIndex: 'verifiedAt',
            align: 'center',
            render: v =>
                v ? (
                    <Tooltip title={`Verified ${getComputerFormattedTime(v)}`}>
                        <CheckCircleFilled style={{ color: PRIMARY_COLOR, fontSize: 16 }} />
                    </Tooltip>
                ) : (
                    EMPTY_VALUE
                ),
        },
        {
            title: 'Error',
            dataIndex: 'processingError',
            render: v =>
                v ? (
                    <Text ellipsis={{ tooltip: v }} style={{ maxWidth: 240 }}>
                        {v}
                    </Text>
                ) : (
                    EMPTY_VALUE
                ),
        },
        { title: 'Location Name', dataIndex: 'siteName', render: v => (v ? <Text strong>{v}</Text> : EMPTY_VALUE) },
        { title: 'Site/Store Number', dataIndex: 'siteNumber', render: v => v || EMPTY_VALUE },
        { title: 'Billing Service Address', dataIndex: 'address', render: v => v || EMPTY_VALUE },
        { title: 'City', dataIndex: 'city', render: v => v || EMPTY_VALUE },
        { title: 'State', dataIndex: 'state', render: v => v || EMPTY_VALUE },
        { title: 'Vendor Name', dataIndex: 'vendor', render: v => v || EMPTY_VALUE },
        { title: 'Utility Account Number', dataIndex: 'accountNumber', render: v => v || EMPTY_VALUE },
        { title: 'Meter Number', dataIndex: 'meterNumber', render: v => v || EMPTY_VALUE },
        { title: 'Enrollment ID', dataIndex: 'enrollmentId', render: v => v || EMPTY_VALUE },
        { title: 'Rate Class', dataIndex: 'rateClass', render: v => displayRateClass(v) ?? EMPTY_VALUE },
        { title: 'Zip Code', dataIndex: 'zipCode', render: v => v || EMPTY_VALUE },
        { title: 'Billing kW', dataIndex: 'billingKw', align: 'right', render: v => (v == null ? EMPTY_VALUE : v) },
        { title: 'Billing kWh', dataIndex: 'billingKwh', align: 'right', render: v => (v == null ? EMPTY_VALUE : v.toLocaleString()) },
        {
            title: 'Billing Period',
            key: 'period',
            render: (_, r) =>
                r.billingPeriodStart ? (
                    <Text>
                        {defaultDateFormat(new Date(r.billingPeriodStart), EMPTY_VALUE, BILL_DATE_FORMAT)} →{' '}
                        {r.billingPeriodEnd ? defaultDateFormat(new Date(r.billingPeriodEnd), EMPTY_VALUE, BILL_DATE_FORMAT) : EMPTY_VALUE}
                    </Text>
                ) : (
                    EMPTY_VALUE
                ),
        },
        { title: 'Customer', dataIndex: 'customerName', render: v => v || EMPTY_VALUE },
        { title: 'Market', dataIndex: 'market', render: v => v || EMPTY_VALUE },
        { title: 'Zone', dataIndex: 'zone', render: v => v || EMPTY_VALUE },
        { title: 'Utility', dataIndex: 'utility', render: v => v || EMPTY_VALUE },
    ];

    const trackingMessage = allDone
        ? 'Re-parsing complete.'
        : `Tracking re-parse progress for ${plural(trackedCount, 'bill')} — refreshing every minute.`;

    return (
        <Modal
            title={copy.title(tableRows.length)}
            open
            width="min(1600px, 95vw)"
            destroyOnHidden
            onCancel={onClose}
            footer={
                isTracking
                    ? [
                          <Button key="close" type="primary" onClick={onClose}>
                              Close
                          </Button>,
                      ]
                    : [
                          <Button key="cancel" onClick={onClose} disabled={confirming}>
                              Cancel
                          </Button>,
                          <Button key="confirm" type="primary" loading={confirming} onClick={handleConfirm}>
                              {copy.confirmLabel}
                          </Button>,
                      ]
            }
        >
            <Space direction="vertical" style={{ width: '100%' }}>
                {isTracking && (
                    <Alert
                        type={allDone ? 'success' : 'info'}
                        showIcon
                        icon={allDone ? undefined : <SyncOutlined spin />}
                        message={trackingMessage}
                    />
                )}
                {ineligibleCount > 0 && (
                    <Alert
                        type="warning"
                        showIcon
                        message={`${ineligibleCount} of ${tableRows.length} selected bills will be skipped: ${copy.ineligibleReason.toLowerCase()}.`}
                    />
                )}
                <Table<Row>
                    size="small"
                    rowKey="id"
                    columns={columns}
                    dataSource={tableRows}
                    scroll={{ x: 'max-content' }}
                    pagination={{ pageSize: 10, showSizeChanger: false }}
                />
            </Space>
        </Modal>
    );
};
